if (!customElements.get('wh-collection')) {
  customElements.define('wh-collection', class extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-filter-form]');
      this.grid = this.querySelector('#ProductGridContainer');
      this.dialog = this.querySelector('dialog');
      this.fields = this.querySelector('[data-filter-fields]');
      this.status = this.querySelector('[data-status]');
      this.media = matchMedia('(max-width: 749px)');
      this.pages = 1;
      this.listeners = new AbortController();
      const options = { signal: this.listeners.signal };
      this.setupSort(options);
      this.moveFields = () => {
        if (!this.fields) return;
        if (!this.media.matches && this.dialog.open) this.dialog.close();
        this.querySelector(this.media.matches ? '[data-filter-dialog-body]' : '[data-filter-home]').append(this.fields);
        if (this.media.matches && this.fields.children.length === 1) this.fields.querySelector('details').open = true;
      };
      this.moveFields();
      this.media.addEventListener('change', this.moveFields, options);
      this.querySelector('[data-open-filters]').hidden = !this.fields?.children.length;
      this.form.addEventListener('submit', (event) => { event.preventDefault(); this.apply(); }, options);
      this.addEventListener('change', (event) => {
        if (event.target.matches('[form], select[name="sort_by"]')) this.apply();
      }, options);
      this.addEventListener('input', (event) => {
        if (!event.target.matches('[data-filter-search]')) return;
        const details = event.target.closest('details');
        const query = this.normalize(event.target.value);
        let visible = 0;
        details.querySelectorAll('[data-filter-option]').forEach((label) => {
          label.hidden = !this.normalize(label.dataset.filterOption).includes(query);
          if (!label.hidden) visible++;
        });
        details.querySelector('[data-filter-no-match]').hidden = visible > 0;
      }, options);
      this.addEventListener('click', (event) => {
        const link = event.target.closest('a[data-filter-link], a[data-next]');
        if (link && !event.metaKey && !event.ctrlKey && !event.shiftKey && event.button === 0) {
          event.preventDefault();
          if (link.hasAttribute('data-next')) this.loadMore();
          else this.navigate(new URL(link.href));
        }
        if (event.target.closest('[data-open-filters]')) this.dialog.showModal();
        if (event.target.closest('[data-close-filters], [data-apply-filters]')) this.dialog.close();
      }, options);
      this.dialog.addEventListener('close', () => this.querySelector('[data-open-filters]').focus({ preventScroll: true }), options);
      document.addEventListener('click', (event) => {
        if (!this.sortControl?.contains(event.target)) this.closeSort();
        if (this.dialog.open) return;
        this.fields?.querySelectorAll('details[open]').forEach((details) => { if (!details.contains(event.target)) details.open = false; });
      }, options);
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !this.dialog.open) {
          const details = event.target.closest('details[open]');
          if (details) { details.open = false; details.querySelector('summary').focus(); }
        }
      }, options);
      window.addEventListener('pagehide', () => this.save(), options);
      window.addEventListener('popstate', () => this.restoreNavigation(), options);
      this.syncCount();
      this.restore();
    }
    setupSort(options) {
      this.sortControl = this.querySelector('[data-sort]');
      this.sortSelect = this.sortControl?.querySelector('select');
      if (!this.sortSelect) return;
      this.sortToggle = document.createElement('button');
      this.sortToggle.type = 'button';
      this.sortToggle.id = `${this.sortSelect.id}-toggle`;
      this.sortToggle.className = 'wh-collection__sort-toggle';
      this.sortToggle.setAttribute('aria-haspopup', 'menu');
      this.sortToggle.setAttribute('aria-expanded', 'false');
      this.sortText = document.createElement('span');
      this.sortToggle.append(this.sortText);
      const icon = this.sortControl.querySelector('[data-sort-icon]');
      icon.hidden = false;
      this.sortToggle.append(icon);
      this.sortMenu = document.createElement('div');
      this.sortMenu.id = `${this.sortSelect.id}-menu`;
      this.sortMenu.className = 'wh-collection__sort-menu';
      this.sortMenu.setAttribute('role', 'menu');
      this.sortMenu.setAttribute('aria-label', window.whThemeText('wh_collection_sort_accessible'));
      this.sortMenu.hidden = true;
      this.sortToggle.setAttribute('aria-controls', this.sortMenu.id);
      for (const option of this.sortSelect.options) {
        const button = document.createElement('button');
        button.type = 'button';
        button.tabIndex = -1;
        button.setAttribute('role', 'menuitemradio');
        button.dataset.sortValue = option.value;
        button.textContent = option.textContent;
        button.addEventListener('click', () => {
          this.sortSelect.value = option.value;
          this.syncSort();
          this.closeSort(true);
          this.apply();
        }, options);
        this.sortMenu.append(button);
      }
      this.sortControl.append(this.sortToggle, this.sortMenu);
      this.sortControl.querySelector('label').htmlFor = this.sortToggle.id;
      this.sortSelect.hidden = true;
      this.syncSort();
      this.sortToggle.addEventListener('click', () => this.sortMenu.hidden ? this.openSort() : this.closeSort(), options);
      this.sortControl.addEventListener('keydown', (event) => {
        const items = Array.from(this.sortMenu.querySelectorAll('button'));
        const index = items.indexOf(document.activeElement);
        if (event.key === 'Escape' && !this.sortMenu.hidden) {
          event.preventDefault(); this.closeSort(true);
        } else if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
          event.preventDefault();
          if (this.sortMenu.hidden) { this.openSort(event.key === 'ArrowUp'); return; }
          const target = event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1 : (index + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
          items[target].focus();
        } else if (event.key === 'Tab' && !this.sortMenu.hidden) {
          this.closeSort(true);
        }
      }, options);
      this.sortControl.addEventListener('focusout', (event) => {
        if (!this.sortControl.contains(event.relatedTarget)) this.closeSort();
      }, options);
    }
    syncSort() {
      if (!this.sortSelect) return;
      const label = this.sortSelect.selectedOptions[0]?.textContent || '';
      this.sortText.textContent = label;
      this.sortToggle.setAttribute('aria-label', window.whThemeText('wh_sort_selected', { label }));
      this.sortMenu.querySelectorAll('button').forEach((button) => button.setAttribute('aria-checked', String(button.dataset.sortValue === this.sortSelect.value)));
    }
    openSort(last = false) {
      this.sortMenu.hidden = false;
      this.sortToggle.setAttribute('aria-expanded', 'true');
      const items = this.sortMenu.querySelectorAll('button');
      (last ? items[items.length - 1] : this.sortMenu.querySelector('[aria-checked="true"]') || items[0]).focus();
    }
    closeSort(restoreFocus = false) {
      if (!this.sortMenu || this.sortMenu.hidden) return;
      this.sortMenu.hidden = true;
      this.sortToggle.setAttribute('aria-expanded', 'false');
      if (restoreFocus) this.sortToggle.focus({ preventScroll: true });
    }
    normalize(text) {
      let value = text.toLowerCase().replace(/ß/g, 'ss').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/ae|oe|ue/g, (pair) => pair[0]);
      if (value.includes('bayerischer gebirgsschweisshund')) value += ' bgs';
      if (value.includes('kleiner munsterlander')) value += ' klm';
      if (value.includes('dackel')) value += ' teckel';
      return value;
    }
    syncCount() {
      const checked = Array.from(this.querySelectorAll('input[name]:checked'));
      this.querySelector('[data-filter-count]').textContent = checked.length ? `(${checked.length})` : '';

    }
    apply() {
      const url = new URL(location.href);
      const params = new URLSearchParams(new FormData(this.form));
      for (const [key, value] of Array.from(params)) if (!value) params.delete(key);
      url.search = params.toString();
      this.navigate(url);
    }
    async navigate(url, push = true) {
      if (push) this.save();
      url.searchParams.delete('section_id');
      url.searchParams.delete('page');
      this.request?.abort();
      this.request = new AbortController();
      const request = this.request;
      this.status.textContent = window.whThemeText('wh_collection_loading');
      this.grid.setAttribute('aria-busy', 'true');
      this.querySelector('[data-apply-filters]').disabled = true;
      try {
        const source = await this.fetchSection(url, request.signal);
        if (request.signal.aborted) return;
        if (push) history.pushState({ whCollection: true }, '', url);
        this.grid.innerHTML = source.querySelector('#ProductGridContainer').innerHTML;
        this.pages = 1;
        for (const selector of ['[data-active-filters]', '[data-product-count]', '[data-apply-filters]']) {
          const current = this.querySelector(selector), incoming = source.querySelector(selector);
          if (current && incoming) current.innerHTML = incoming.innerHTML;
        }
        // Keep open popovers, typing focus and scroll position while refreshing counts.
        this.fields?.querySelectorAll('input[name]').forEach((input) => {
          const replacement = Array.from(source.querySelectorAll('[data-filter-fields] input[name]')).find((item) => item.name === input.name && item.value === input.value);
          if (input.type === 'checkbox') input.checked = replacement?.checked || false;
          else input.value = replacement?.value || '';
          input.disabled = replacement ? replacement.disabled : true;
          const count = input.closest('label').querySelector('[data-option-count]');
          if (count) count.textContent = replacement?.closest('label').querySelector('[data-option-count]')?.textContent || '(0)';
        });
        const sort = this.querySelector('[name="sort_by"]');
        if (sort) sort.value = source.querySelector('[name="sort_by"]').value;
        this.syncSort();
        this.syncCount();
        this.status.textContent = this.querySelector('[data-product-count]').textContent;
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.warn('Collection update failed', error);
          this.status.textContent = window.whThemeText('wh_collection_reloading');
          location.assign(url);
        }
      } finally {
        if (this.request === request) {
          this.grid.removeAttribute('aria-busy');
          this.querySelector('[data-apply-filters]').disabled = false;
        }
      }
    }
    async fetchSection(url, signal) {
      const target = new URL(url);
      target.searchParams.set('section_id', this.dataset.section);
      if (this.dataset.themeId && this.dataset.themeRole && !['main', 'live'].includes(this.dataset.themeRole)) target.searchParams.set('preview_theme_id', this.dataset.themeId);
      const response = await fetch(target, { signal });
      if (!response.ok) throw new Error('Collection unavailable');
      const source = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('wh-collection');
      if (!source?.querySelector('#product-grid')) throw new Error('Collection missing');
      return source;
    }
    async loadMore(restore = false) {
      const next = this.querySelector('[data-next]');
      if (!next || this.loadingMore) return false;
      this.loadingMore = true;
      next.setAttribute('aria-disabled', 'true');
      next.textContent = window.whThemeText('wh_collection_loading_products');
      const before = this.grid.querySelectorAll('[data-product-id]').length;
      const filterUrl = location.href;
      try {
        const source = await this.fetchSection(new URL(next.href));
        if (location.href !== filterUrl) return false;
        const existing = new Set(Array.from(this.grid.querySelectorAll('[data-product-id]')).map((item) => item.dataset.productId));
        source.querySelectorAll('#product-grid > li').forEach((item) => { if (!existing.has(item.dataset.productId)) this.grid.querySelector('#product-grid').append(item); });
        this.grid.querySelector('.wh-collection__pagination').innerHTML = source.querySelector('.wh-collection__pagination').innerHTML;
        this.pages++;
        if (!restore) {
          this.status.textContent = window.whThemeText('wh_products_loaded', { count: this.grid.querySelectorAll('[data-product-id]').length - before });
          this.grid.querySelectorAll('#product-grid > li')[before]?.querySelector('a')?.focus({ preventScroll: true });
        }
        this.save();
        return true;
      } catch (error) {
        next.removeAttribute('aria-disabled');
        next.textContent = window.whThemeText('wh_collection_retry');
        this.status.textContent = window.whThemeText('wh_collection_load_error');
        return false;
      } finally { this.loadingMore = false; }
    }
    stateKey() { return `wh-collection:${location.pathname}${location.search}`; }
    save() {
      if (this.restoring) return;
      const search = this.querySelector('wh-collection-search');
      try { sessionStorage.setItem(this.stateKey(), JSON.stringify({ time: Date.now(), pages: this.pages, y: scrollY, query: search?.input?.value || '', shown: search?.shown || 24 })); } catch (_) { /* Storage is optional. */ }
    }
    async restoreNavigation() {
      await this.navigate(new URL(location.href), false);
      await this.restore();
    }
    async restore() {
      let saved;
      try { saved = JSON.parse(sessionStorage.getItem(this.stateKey())); } catch (_) { return; }
      if (!saved || Date.now() - saved.time > 30 * 60 * 1000) return;
      this.restoring = true;
      try {
        while (this.pages < Math.min(saved.pages, 30)) { if (!await this.loadMore(true)) break; }
        await customElements.whenDefined('wh-collection-search');
        const search = this.querySelector('wh-collection-search');
        if (search?.input) {
          search.input.value = saved.query;
          await search.search();
          while (search.matches && saved.shown > search.shown && search.shown < search.matches.length) search.showMore();
        }
        requestAnimationFrame(() => scrollTo({ top: saved.y, behavior: 'instant' }));
      } finally { this.restoring = false; }
    }
    disconnectedCallback() { this.listeners?.abort(); this.request?.abort(); }
  });
}
